import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ═══════════════════════════════════════════════════════════════
// TRIAGE — Komponenten (integriert als Tab)
// ═══════════════════════════════════════════════════════════════

const PRIO_COLORS = {
  DRINGEND: { bg: '#fde8e8', border: '#dc2626', text: '#dc2626', badge: '#dc2626' },
  HOCH:     { bg: '#fff4e6', border: '#ea580c', text: '#ea580c', badge: '#ea580c' },
  NORMAL:   { bg: 'rgba(0,168,126,0.10)', border: '#00a87e', text: '#007a5a', badge: '#00a87e' },
  NIEDRIG:  { bg: '#f3f4f6', border: '#6b7280', text: '#6b7280', badge: '#6b7280' },
};

function TriageCard({ card, team, onDecision, decision }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const [editedDraft, setEditedDraft] = useState(card.draft || '');
  const [editedTo, setEditedTo] = useState((card.draft_to || []).join(', '));
  const [editedCc, setEditedCc] = useState((card.draft_cc || []).join(', '));
  const [replyType, setReplyType] = useState(card.reply_type || 'reply');
  const [wlTo, setWlTo] = useState(card.wl_to || (card.is_invoice ? 'rechnungen@fleet-immobilien.de' : ''));

  const prio = PRIO_COLORS[card.priority?.toUpperCase()] || PRIO_COLORS.NIEDRIG;
  const isDone = !!decision;

  const handleAction = (action) => {
    onDecision(card.id, {
      id: card.id, message_id: card.message_id, subject: card.subject, action,
      reply_type: replyType,
      draft: editedDraft,
      draft_to: editedTo.split(',').map(s => s.trim()).filter(Boolean),
      draft_cc: editedCc.split(',').map(s => s.trim()).filter(Boolean),
      wl_to: wlTo,
      wl_text: card.is_internal
        ? `${editedDraft || card.suggestion || 'Zur Kenntnis'}`
        : `Zur Kenntnis.\n\n---\nOriginal: ${card.subject}\nVon: ${card.from_name} (${card.from_email})`,
    });
  };

  const btnStyle = (bg, color) => ({
    padding: '8px 14px', fontSize: 11, fontWeight: 600, background: bg, color,
    border: 'none', borderRadius: 4, cursor: isDone ? 'default' : 'pointer',
    fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.3px',
    opacity: isDone ? 0.5 : 1,
  });

  const toggleBtn = { background: 'none', border: '1px solid var(--brand-warm-gray)', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: 'var(--brand-navy-70)', fontFamily: 'var(--font-body)' };
  const inputSm = { padding: '8px 12px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontFamily: 'var(--font-body)' };

  return (
    <div style={{
      background: isDone ? '#f9fafb' : '#fff',
      border: `1px solid ${isDone ? 'var(--brand-warm-gray)' : prio.border}`,
      borderLeft: `4px solid ${prio.border}`, borderRadius: 8,
      padding: '16px 20px', marginBottom: 12, opacity: isDone ? 0.7 : 1,
      transition: 'all 0.2s', position: 'relative',
    }}>
      {isDone && <div style={{ position: 'absolute', top: 12, right: 16, background: 'var(--brand-success)', color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>✓ {decision.action}</div>}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: prio.badge, color: '#fff', textTransform: 'uppercase' }}>{card.priority} ({card.score})</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-navy)', flex: 1 }}>{card.subject}</span>
        <span style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0 }}>{timeAgo(card.received)}</span>
      </div>

      {/* Tags */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
        {!card.is_read && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: 'var(--brand-gold-10)', color: 'var(--brand-gold)', fontWeight: 600 }}>NEU</span>}
        {card.is_internal && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: '#e8f0fe', color: '#1a73e8', fontWeight: 600 }}>INTERN</span>}
        {card.has_attachments && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: '#f3f4f6', color: '#6b7280', fontWeight: 600 }}>ANHANG</span>}
        {card.is_invoice && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: '#fff4e6', color: '#ea580c', fontWeight: 600 }}>RECHNUNG</span>}
        {card.already_replied && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: 'var(--brand-success-bg)', color: 'var(--brand-success)', fontWeight: 600 }}>BEANTWORTET</span>}
      </div>

      <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 10 }}>Von: <strong style={{ color: 'var(--brand-navy)' }}>{card.from_name}</strong> ({card.from_email})</div>
      <div style={{ fontSize: 13, color: 'var(--brand-navy)', marginBottom: 6, lineHeight: 1.5 }}>{card.summary}</div>
      <div style={{ fontSize: 12, color: prio.text, fontWeight: 500, marginBottom: 12 }}>→ {card.suggestion}</div>

      {/* Toggles */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => setShowOriginal(!showOriginal)} style={toggleBtn}>{showOriginal ? '▲' : '▼'} Original</button>
        <button onClick={() => setShowDraft(!showDraft)} style={toggleBtn}>{showDraft ? '▲' : '▼'} Entwurf</button>
        <select value={replyType} onChange={e => setReplyType(e.target.value)} style={{ ...inputSm, fontSize: 11 }}>
          <option value="reply">Reply</option>
          <option value="reply_all">Reply All</option>
          <option value="new">Neue Mail</option>
        </select>
      </div>

      {showOriginal && <div style={{ background: 'var(--brand-cream)', borderRadius: 6, padding: 12, marginBottom: 12, fontSize: 12, color: 'var(--brand-text)', lineHeight: 1.5, whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>{card.original_preview || card.preview || '(Kein Inhalt)'}</div>}

      {showDraft && (
        <div style={{ background: '#f8f9fa', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: 'var(--brand-text-muted)', fontWeight: 600 }}>AN:</label>
            <input value={editedTo} onChange={e => setEditedTo(e.target.value)} style={{ ...inputSm, flex: 1, minWidth: 0 }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: 'var(--brand-text-muted)', fontWeight: 600 }}>CC:</label>
            <input value={editedCc} onChange={e => setEditedCc(e.target.value)} style={{ ...inputSm, flex: 1, minWidth: 0 }} />
          </div>
          <div style={{ borderTop: '1px solid var(--brand-warm-gray)', paddingTop: 8 }}>
            <textarea value={editedDraft} onChange={e => setEditedDraft(e.target.value)} rows={5}
              style={{ width: '100%', padding: 8, fontSize: 13, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontFamily: 'var(--font-body)', resize: 'vertical', lineHeight: 1.6, color: 'var(--brand-navy)' }} />
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => handleAction('senden')} disabled={isDone} style={btnStyle('var(--brand-navy)', '#fff')}>Antworten</button>
        <button onClick={() => handleAction('senden+wl')} disabled={isDone} style={btnStyle('var(--brand-gold)', 'var(--brand-navy)')}>Antw. + WL</button>
        <button onClick={() => handleAction('wl')} disabled={isDone} style={btnStyle('#e8f0fe', '#1a73e8')}>Nur WL</button>
        <button onClick={() => handleAction('anpassen')} disabled={isDone} style={btnStyle('var(--brand-cream)', 'var(--brand-navy)')}>Anpassen</button>
        <button onClick={() => handleAction('skip')} disabled={isDone} style={btnStyle('#f3f4f6', '#6b7280')}>Skip</button>
        <button onClick={() => handleAction('archiv')} disabled={isDone} style={btnStyle('var(--brand-warm-gray)', 'var(--brand-navy)')}>Archiv</button>
        <select value={wlTo} onChange={e => setWlTo(e.target.value)} style={{ ...inputSm, fontSize: 11, minWidth: 160 }}>
          <option value="">- WL an -</option>
          {team.map(t => <option key={t.email} value={t.email}>{t.name}</option>)}
        </select>
      </div>
    </div>
  );
}

function TriageTab({ token }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [execResult, setExecResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [styleExists, setStyleExists] = useState(null); // null=loading, true/false

  // Beim Mount: Stil-Status + gecachte Triage-Ergebnisse laden
  useEffect(() => {
    fetch(`${API_URL}/mail/style-profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { exists: false })
      .then(d => setStyleExists(d.exists))
      .catch(() => setStyleExists(false));

    // Gecachte Triage laden
    fetch(`${API_URL}/mail/triage/cached`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.cards && data.cards.length > 0) {
          setCards(data.cards);
          setStats(data.stats);
          setTeam(data.team || []);
        }
      })
      .catch(() => {});

    // Gecachte Entscheidungen laden
    fetch(`${API_URL}/mail/triage/saved-decisions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { decisions: {} })
      .then(data => {
        if (data.decisions && Object.keys(data.decisions).length > 0) {
          setDecisions(data.decisions);
        }
      })
      .catch(() => {});
  }, [token]);

  const resetStyle = useCallback(async () => {
    await fetch(`${API_URL}/mail/style-profile`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setStyleExists(false);
  }, [token]);

  const startTriage = useCallback(async () => {
    setLoading(true); setError(null); setCards([]); setDecisions({}); setExecResult(null);
    setProgress({ percent: 5, message: 'Berechtigungen werden geprüft...' });

    // Progress-Polling starten
    const pollId = setInterval(async () => {
      try {
        const pr = await fetch(`${API_URL}/mail/triage/progress`, { headers: { Authorization: `Bearer ${token}` } });
        if (pr.ok) {
          const pd = await pr.json();
          if (pd.percent > 0) setProgress({ percent: pd.percent, message: pd.message });
        }
      } catch (_) {}
    }, 2000);

    try {
      const res = await fetch(`${API_URL}/mail/triage`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 403) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'E-Mail Triage ist für Ihre Benutzergruppe nicht freigeschaltet.');
      }
      if (!res.ok) throw new Error(`Fehler ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setCards(data.cards || []); setStats(data.stats); setTeam(data.team || []);
      setStyleExists(true); // Stil wurde beim ersten Mal erstellt
    } catch (err) { setError(err.message); }
    clearInterval(pollId);
    setProgress(null);
    setLoading(false);
  }, [token]);

  const handleDecision = useCallback((cardId, decision) => {
    setDecisions(prev => {
      const next = { ...prev, [cardId]: decision };
      // Entscheidungen persistent speichern
      fetch(`${API_URL}/mail/triage/save-decisions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ decisions: next }),
      }).catch(() => {});
      return next;
    });
  }, [token]);

  const executeTriage = useCallback(async () => {
    const list = Object.values(decisions);
    if (!list.length) return;
    setExecuting(true); setExecResult(null);
    try {
      const res = await fetch(`${API_URL}/mail/triage/execute`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ decisions: list }),
      });
      if (!res.ok) throw new Error(`Fehler ${res.status}`);
      setExecResult(await res.json());
    } catch (err) { setError(err.message); }
    setExecuting(false);
  }, [token, decisions]);

  const bulkAction = (action, filter) => {
    const newD = { ...decisions };
    cards.forEach(c => {
      if (newD[c.id]) return;
      if (filter && !filter(c)) return;
      newD[c.id] = { id: c.id, message_id: c.message_id, subject: c.subject, action, reply_type: 'reply', draft: '', draft_to: [], draft_cc: [], wl_to: '', wl_text: '' };
    });
    setDecisions(newD);
    // Persistent speichern
    fetch(`${API_URL}/mail/triage/save-decisions`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ decisions: newD }),
    }).catch(() => {});
  };

  const decisionCount = Object.keys(decisions).length;
  const cardStyle = { background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' };

  return (
    <div>
      {/* Start-Button + Stil-Status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 13, color: 'var(--brand-text-muted)', margin: 0 }}>
            {styleExists
              ? 'Dein Schreibstil ist gespeichert. Posteingang wird analysiert.'
              : 'Beim ersten Start wird dein Schreibstil aus gesendeten Mails gelernt.'}
          </p>
          {styleExists && (
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'var(--brand-success-bg)', color: 'var(--brand-success)', fontWeight: 600 }}>
              Stil erkannt
            </span>
          )}
          {styleExists && isAdmin && (
            <button onClick={resetStyle} style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 4,
              border: '1px solid var(--brand-warm-gray)', background: '#fff',
              color: 'var(--brand-navy-70)', cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}>Stil neu erkennen</button>
          )}
        </div>
        <button onClick={startTriage} disabled={loading} style={{
          padding: '10px 24px', fontSize: 12, fontWeight: 600,
          background: loading ? 'var(--brand-warm-gray)' : 'var(--brand-gold)',
          color: loading ? 'var(--brand-text-muted)' : 'var(--brand-navy)',
          border: 'none', borderRadius: 4, cursor: loading ? 'wait' : 'pointer',
          textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)', flexShrink: 0,
        }}>
          {loading ? 'Wird analysiert...' : cards.length > 0 ? 'Posteingang neu laden' : 'Triage starten'}
        </button>
      </div>

      {error && <div style={{ ...cardStyle, padding: '12px 16px', marginBottom: 16, borderLeft: '3px solid var(--brand-danger)', color: 'var(--brand-danger)', fontSize: 13 }}>{error}</div>}

      {/* Stats */}
      {stats && (
        <div className="stats-grid-4" style={{ marginBottom: 16 }}>
          {[
            { label: 'Dringend', value: stats.dringend, color: '#dc2626' },
            { label: 'Hoch', value: stats.hoch, color: '#ea580c' },
            { label: 'Normal', value: stats.normal, color: '#00a87e' },
            { label: 'Niedrig', value: stats.niedrig, color: '#6b7280' },
          ].map(s => (
            <div key={s.label} style={{ ...cardStyle, padding: '12px 16px', borderTop: `3px solid ${s.color}`, marginBottom: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{s.label}</div>
              <div style={{ fontFamily: 'var(--font-headline)', fontSize: 28, color: s.value > 0 ? s.color : 'var(--brand-navy)' }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Action Bar */}
      {cards.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 16, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>{decisionCount}/{cards.length} bearbeitet</div>
          <div style={{ flex: 1 }} />
          <button onClick={() => bulkAction('skip')} style={{ padding: '5px 12px', fontSize: 11, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Alle offenen → Skip</button>
          <button onClick={() => bulkAction('archiv', c => c.priority?.toUpperCase() === 'NIEDRIG')} style={{ padding: '5px 12px', fontSize: 11, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>NIEDRIG → Archiv</button>
          {decisionCount > 0 && (
            <button onClick={executeTriage} disabled={executing} style={{
              padding: '8px 20px', fontSize: 12, fontWeight: 600,
              background: executing ? 'var(--brand-warm-gray)' : 'var(--brand-success)',
              color: '#fff', border: 'none', borderRadius: 4, cursor: executing ? 'wait' : 'pointer',
              fontFamily: 'var(--font-body)', textTransform: 'uppercase',
            }}>{executing ? 'Wird ausgeführt...' : `${decisionCount} Aktionen ausführen`}</button>
          )}
        </div>
      )}

      {/* Exec Result */}
      {execResult && (
        <div style={{ ...cardStyle, marginBottom: 16, padding: '16px 20px', borderLeft: '3px solid var(--brand-success)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8 }}>Ausführung abgeschlossen</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
            <span>✓ {execResult.summary?.replied || 0} Antworten</span>
            <span>→ {execResult.summary?.forwarded || 0} Weiterleitungen</span>
            <span>📁 {execResult.summary?.archived || 0} Archiviert</span>
            <span>⏭ {execResult.summary?.skipped || 0} Übersprungen</span>
            {execResult.summary?.errors > 0 && <span style={{ color: 'var(--brand-danger)' }}>⚠ {execResult.summary.errors} Fehler</span>}
          </div>
        </div>
      )}

      {/* Loading mit Fortschrittsbalken */}
      {loading && (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: 14, color: 'var(--brand-navy)', fontWeight: 500, marginBottom: 16 }}>
            {progress?.message || 'Posteingang wird geladen...'}
          </div>
          <div style={{ width: '100%', maxWidth: 400, margin: '0 auto', height: 8, background: 'var(--brand-cream)', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{
              height: '100%', borderRadius: 4,
              background: 'var(--brand-gold)',
              width: `${progress?.percent || 5}%`,
              transition: 'width 0.5s ease',
            }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
            {progress?.percent || 5}% - Stil wird analysiert, Mails werden bewertet...
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && cards.length === 0 && !error && (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '4rem' }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>📬</div>
          <div style={{ fontFamily: 'var(--font-headline)', fontSize: 18, color: 'var(--brand-navy)', marginBottom: 8 }}>E-Mail Triage</div>
          <div style={{ fontSize: 13, color: 'var(--brand-text-muted)', maxWidth: 420, margin: '0 auto' }}>
            Klicke auf "Triage starten". Die KI liest deinen Posteingang, lernt aus deinen gesendeten Mails deinen Schreibstil und erstellt für jede Mail einen Antwortentwurf in deinem Stil.
          </div>
        </div>
      )}

      {/* Cards */}
      {cards.map(c => <TriageCard key={c.id} card={c} team={team} onDecision={handleDecision} decision={decisions[c.id]} />)}
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = (now - d) / 1000;
  if (diff < 60) return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
  if (diff < 172800) return 'gestern';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

const priorityConfig = {
  urgent:    { color: '#c0392b', bg: '#fde8e8', label: 'DRINGEND' },
  important: { color: '#e67e22', bg: '#fef4e6', label: 'WICHTIG' },
  normal:    { color: 'var(--brand-navy)', bg: 'var(--brand-cream, #fafaf6)', label: 'NORMAL' },
  low:       { color: 'var(--brand-text-muted)', bg: '#f5f5f5', label: 'NIEDRIG' },
};

// ── Tab: Posteingang ──
function InboxTab({ token }) {
  const [emails, setEmails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/mail/inbox?page=${page}&page_size=30`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(data => {
        setEmails(data.messages);
        setTotalPages(data.total_pages);
        setTotal(data.total);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token, page]);

  const card = {
    background: '#fff', border: '1px solid var(--brand-warm-gray)',
    borderRadius: 8, padding: '1.25rem',
  };

  if (loading) return (
    <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
      Posteingang wird geladen...
    </div>
  );

  if (!emails || emails.length === 0) return (
    <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
      Keine E-Mails gefunden.
    </div>
  );

  return (
    <>
      {total > 0 && (
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 10 }}>{total} Mails im Posteingang</div>
      )}
      <div style={card}>
        {emails.map((mail, i) => (
          <div key={mail.id} style={{
            display: 'flex', gap: 12, padding: '10px 4px',
            borderBottom: i < emails.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none',
            background: mail.is_read ? 'transparent' : 'rgba(193,167,111,0.04)',
          }}>
            <div style={{ width: 6, flexShrink: 0, paddingTop: 6 }}>
              {!mail.is_read && (
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand-gold)' }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{
                  fontSize: 13, fontWeight: mail.is_read ? 400 : 600,
                  color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {mail.from_name || mail.from_email}
                </span>
                {mail.is_external && (
                  <span style={{
                    fontSize: 9, padding: '1px 5px', borderRadius: 3,
                    background: 'var(--brand-gold-10, #f5f0e0)', color: 'var(--brand-gold)',
                    fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0,
                  }}>extern</span>
                )}
                {mail.importance === 'high' && (
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#fde8e8', color: 'var(--brand-danger)', fontWeight: 600, flexShrink: 0 }}>wichtig</span>
                )}
                {mail.flagged && <span style={{ fontSize: 12, flexShrink: 0 }}>&#9873;</span>}
                {mail.has_attachments && <span style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0 }}>&#128206;</span>}
              </div>
              <div style={{
                fontSize: 13, fontWeight: mail.is_read ? 400 : 500,
                color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2,
              }}>{mail.subject}</div>
              <div style={{
                fontSize: 12, color: 'var(--brand-text-muted)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{mail.preview}</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0, paddingTop: 2, whiteSpace: 'nowrap' }}>
              {timeAgo(mail.received)}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}
          >Zurück</button>
          <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>Seite {page} von {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}
          >Weiter</button>
        </div>
      )}
    </>
  );
}

// ── Tab: Morning Briefing ──
function BriefingTab({ token }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [tasksDone, setTasksDone] = useState([]);
  const [highlightsDone, setHighlightsDone] = useState([]);
  const [modalMailId, setModalMailId] = useState(null);
  const [modalCompose, setModalCompose] = useState(null);

  const loadBriefing = () => {
    fetch(`${API_URL}/mail/briefing`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(data => { setBriefing(data); setTasksDone(data.tasks_done || []); setHighlightsDone(data.highlights_done || []); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  };

  const toggleHighlight = (e, index) => {
    e.stopPropagation();
    const done = !highlightsDone.includes(index);
    const newDone = done ? [...highlightsDone, index] : highlightsDone.filter(i => i !== index);
    setHighlightsDone(newDone);
    fetch(`${API_URL}/mail/briefing/toggle-highlight`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ index, done }),
    }).catch(() => {});
  };

  useEffect(() => {
    if (!token) return;
    loadBriefing();
  }, [token]);

  const toggleTask = (index) => {
    const done = !tasksDone.includes(index);
    const newDone = done ? [...tasksDone, index] : tasksDone.filter(i => i !== index);
    setTasksDone(newDone);
    fetch(`${API_URL}/mail/briefing/toggle-task`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ index, done }),
    }).catch(() => {});
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetch(`${API_URL}/mail/briefing/refresh`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.status === 429) return r.json().then(d => { throw new Error(d.detail); });
        if (!r.ok) throw new Error(`Fehler ${r.status}`);
        return r.json();
      })
      .then(data => { setBriefing(data); setRefreshing(false); })
      .catch(err => { alert(err.message); setRefreshing(false); });
  };

  const openMail = (mailId) => {
    if (mailId) {
      setModalMailId(mailId);
      setModalCompose(null);
    }
  };

  const card = {
    background: '#fff', border: '1px solid var(--brand-warm-gray)',
    borderRadius: 8, padding: '1.25rem',
  };

  if (loading) return (
    <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
      Briefing wird geladen...
    </div>
  );

  if (error) return (
    <div style={{ ...card, padding: '2rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
      Briefing konnte nicht geladen werden. ({error})
    </div>
  );

  if (!briefing) return null;

  const generatedAt = briefing.generated_at ? new Date(briefing.generated_at) : null;
  const timeStr = generatedAt ? generatedAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
  const limitLabel = briefing.refresh_limit === -1
    ? 'unbegrenzt'
    : `${briefing.refresh_count}/${briefing.refresh_limit} Aktualisierungen heute`;

  return (
    <div>
      {/* Status-Leiste */}
      <div className="email-briefing-status" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, padding: '8px 14px',
        background: briefing.cached ? 'var(--brand-cream, #fafaf6)' : 'rgba(193,167,111,0.08)',
        borderRadius: 6, fontSize: 12,
      }}>
        <div style={{ color: 'var(--brand-text-muted)' }}>
          {briefing.cached
            ? <>{`Erstellt um ${timeStr} Uhr`} &middot; {limitLabel}</>
            : <>Gerade frisch erstellt</>
          }
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || !briefing.can_refresh}
          style={{
            padding: '5px 14px', fontSize: 12, fontWeight: 500,
            border: '1px solid var(--brand-warm-gray)', borderRadius: 4,
            background: briefing.can_refresh ? '#fff' : 'var(--brand-warm-gray)',
            color: briefing.can_refresh ? 'var(--brand-navy)' : 'var(--brand-text-muted)',
            cursor: refreshing || !briefing.can_refresh ? 'default' : 'pointer',
            opacity: refreshing ? 0.6 : 1, transition: 'all 0.15s',
          }}
        >
          {refreshing ? 'Wird aktualisiert...'
            : !briefing.can_refresh ? 'Tageslimit erreicht'
            : 'Aktualisieren'}
        </button>
      </div>

      {/* Zusammenfassungs-Karten */}
      <div className="stats-grid-4" style={{ marginBottom: 16 }}>
        <div style={card}>
          <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Analysierte Mails</div>
          <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: 'var(--brand-navy)' }}>{briefing.mail_count ?? '-'}</div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>{briefing.unread_count ?? 0} ungelesen</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 11, color: '#c0392b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Dringend</div>
          <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: briefing.urgent_count > 0 ? '#c0392b' : 'var(--brand-navy)' }}>{briefing.urgent_count ?? 0}</div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>sofort bearbeiten</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 11, color: '#e67e22', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Wichtig</div>
          <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: briefing.important_count > 0 ? '#e67e22' : 'var(--brand-navy)' }}>{briefing.important_count ?? 0}</div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>heute erledigen</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Aufgaben</div>
          <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: 'var(--brand-navy)' }}>
            {tasksDone.length}/{briefing.action_items?.length ?? 0}
          </div>
          <div style={{ fontSize: 12, color: tasksDone.length === (briefing.action_items?.length || 0) && tasksDone.length > 0 ? 'var(--brand-success, #27ae60)' : 'var(--brand-text-muted)', marginTop: 4 }}>
            {tasksDone.length === (briefing.action_items?.length || 0) && tasksDone.length > 0 ? 'alles erledigt' : 'erledigt'}
          </div>
        </div>
      </div>

      {/* Greeting */}
      <div style={{ ...card, marginBottom: 16, fontSize: 14, color: 'var(--brand-navy)', lineHeight: 1.6 }}>
        {briefing.greeting}
      </div>

      {/* Highlights */}
      {briefing.highlights && briefing.highlights.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14 }}>
            Priorisierte Mails - Klick zum Öffnen
          </div>
          {briefing.highlights.map((h, i) => {
            const p = priorityConfig[h.priority] || priorityConfig.normal;
            const isDone = highlightsDone.includes(i);
            const hasMailId = !!h.mail_id;
            return (
              <div key={i}
                onClick={() => hasMailId ? openMail(h.mail_id) : null}
                style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                  padding: '12px 14px', marginBottom: 8,
                  background: isDone ? '#f3f4f6' : p.bg,
                  borderRadius: 6, borderLeft: `3px solid ${isDone ? '#d1d5db' : p.color}`,
                  opacity: isDone ? 0.5 : 1,
                  cursor: hasMailId ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  position: 'relative',
                }}
                onMouseEnter={e => { if (hasMailId && !isDone) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                {/* Checkbox für erledigt */}
                <div onClick={(e) => toggleHighlight(e, i)} style={{
                  width: 20, height: 20, borderRadius: 4, flexShrink: 0, marginTop: 2,
                  border: isDone ? 'none' : `2px solid ${p.color}`,
                  background: isDone ? 'var(--brand-success, #27ae60)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
                title={isDone ? 'Als unerledigt markieren' : 'Als erledigt markieren'}>
                  {isDone && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>&#10003;</span>}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: isDone ? '#9ca3af' : p.color,
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      padding: '1px 6px', borderRadius: 3, background: 'rgba(255,255,255,0.7)',
                    }}>{p.label}</span>
                    <span style={{ fontSize: 12, color: 'var(--brand-text-muted)', textDecoration: isDone ? 'line-through' : 'none' }}>{h.from}</span>
                    {hasMailId && !isDone && (
                      <span style={{ fontSize: 10, color: p.color, fontWeight: 500, marginLeft: 'auto' }}>
                        &#9654; Mail öffnen
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: isDone ? '#9ca3af' : 'var(--brand-navy)', marginBottom: 4, textDecoration: isDone ? 'line-through' : 'none' }}>
                    {h.subject}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--brand-text-muted)', lineHeight: 1.5 }}>
                    {h.summary}
                  </div>
                  {h.action && !isDone && (
                    <div style={{ fontSize: 12, color: p.color, fontWeight: 500, marginTop: 6 }}>
                      &rarr; {h.action}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="stats-grid-2">
        {/* Aufgaben */}
        {briefing.action_items && briefing.action_items.length > 0 && (
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
              Aufgaben für heute
            </div>
            {briefing.action_items.map((item, i) => {
              const done = tasksDone.includes(i);
              return (
                <div key={i} onClick={() => toggleTask(i)} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0',
                  borderBottom: i < briefing.action_items.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none',
                  cursor: 'pointer', userSelect: 'none',
                }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: 4,
                    border: done ? 'none' : '2px solid var(--brand-gold)',
                    background: done ? 'var(--brand-gold)' : 'transparent',
                    flexShrink: 0, marginTop: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}>
                    {done && <span style={{ color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>&#10003;</span>}
                  </div>
                  <span style={{
                    fontSize: 13, lineHeight: 1.4,
                    color: done ? 'var(--brand-text-muted)' : 'var(--brand-navy)',
                    textDecoration: done ? 'line-through' : 'none',
                    transition: 'all 0.15s',
                  }}>{item}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Externe Zusammenfassung */}
        {briefing.external_summary && (
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
              Externe Kommunikation
            </div>
            <div style={{ fontSize: 13, color: 'var(--brand-navy)', lineHeight: 1.6 }}>
              {briefing.external_summary}
            </div>
          </div>
        )}
      </div>

      {/* Mail-Modal (Overlay) */}
      {modalMailId && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(25, 28, 31, 0.6)', backdropFilter: 'blur(2px)',
          zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          paddingTop: 40, overflowY: 'auto',
        }} onClick={() => { setModalMailId(null); setModalCompose(null); }}>
          <div style={{
            width: '100%', maxWidth: 800, margin: '0 20px 40px',
            background: 'var(--brand-surface, #f4f4f4)', borderRadius: 12, padding: 20,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }} onClick={e => e.stopPropagation()}>
            {/* Close Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button onClick={() => { setModalMailId(null); setModalCompose(null); }}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--brand-text-muted)', padding: '4px 8px' }}
                title="Schließen">&#10005;</button>
            </div>
            {modalCompose ? (
              <ComposeEmail token={token} composeData={modalCompose}
                onClose={() => setModalCompose(null)}
                onSent={() => { setModalCompose(null); setModalMailId(null); }} />
            ) : (
              <MailDetailEnhanced
                mailSummary={{ id: modalMailId }}
                token={token}
                onBack={() => { setModalMailId(null); setModalCompose(null); }}
                onCompose={(data) => setModalCompose(data)} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared Mailbox: Mail-Detail-Ansicht ──
function MailDetail({ mail, onBack, isSent }) {
  const body = mail.body || {};
  const htmlContent = body.contentType === 'html' ? body.content : null;
  const textContent = body.contentType === 'text' ? body.content : (mail.bodyPreview || '');

  const sender = mail.from?.emailAddress || {};
  const toList = (mail.toRecipients || []).map(r => r.emailAddress?.address).filter(Boolean);
  const ccList = (mail.ccRecipients || []).map(r => r.emailAddress?.address).filter(Boolean);

  const card = { background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' };

  return (
    <div>
      <button onClick={onBack} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 14px', fontSize: 12, fontWeight: 500,
        border: '1px solid var(--brand-warm-gray)', borderRadius: 4,
        background: '#fff', color: 'var(--brand-navy)', cursor: 'pointer',
        fontFamily: 'var(--font-body)', marginBottom: 16,
      }}>
        &#8592; Zurück zur Liste
      </button>

      <div style={card}>
        {/* Subject */}
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 16, lineHeight: 1.4 }}>
          {mail.subject || '(Kein Betreff)'}
        </div>

        {/* Meta */}
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 6 }}>
          <strong style={{ color: 'var(--brand-navy)' }}>Von:</strong> {sender.name || sender.address || '-'} {sender.address ? `<${sender.address}>` : ''}
        </div>
        {toList.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 6 }}>
            <strong style={{ color: 'var(--brand-navy)' }}>An:</strong> {toList.join(', ')}
          </div>
        )}
        {ccList.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 6 }}>
            <strong style={{ color: 'var(--brand-navy)' }}>CC:</strong> {ccList.join(', ')}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 16 }}>
          <strong style={{ color: 'var(--brand-navy)' }}>Datum:</strong> {new Date(isSent ? (mail.sentDateTime || mail.receivedDateTime) : mail.receivedDateTime).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </div>

        {mail.hasAttachments && (
          <div style={{ fontSize: 11, padding: '4px 10px', borderRadius: 4, background: 'var(--brand-cream)', color: 'var(--brand-navy-70)', display: 'inline-block', marginBottom: 16 }}>
            &#128206; Anhänge vorhanden
          </div>
        )}

        {/* Body */}
        <div style={{ borderTop: '1px solid var(--brand-warm-gray)', paddingTop: 16 }}>
          {htmlContent ? (
            <div
              style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--brand-navy)', overflowWrap: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          ) : (
            <pre style={{
              fontSize: 13, lineHeight: 1.6, color: 'var(--brand-navy)',
              whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontFamily: 'var(--font-body)',
              margin: 0,
            }}>{textContent}</pre>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Shared Mailbox: Mail-Liste (Inbox oder Sent) ──
function SharedMailList({ token, folder }) {
  const [emails, setEmails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedMail, setSelectedMail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const isSent = folder === 'sent';

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setSelectedMail(null);
    const endpoint = isSent ? 'shared/sent' : 'shared/inbox';
    fetch(`${API_URL}/mail/${endpoint}?page=${page}&page_size=30`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(data => {
        setEmails(data.messages);
        setTotalPages(data.total_pages);
        setTotal(data.total);
        setLoading(false);
      })
      .catch(() => { setEmails([]); setLoading(false); });
  }, [token, page, isSent]);

  // Reset page when folder changes
  useEffect(() => { setPage(1); }, [folder]);

  const openMail = async (messageId) => {
    setLoadingDetail(true);
    try {
      const r = await fetch(`${API_URL}/mail/shared/message/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`${r.status}`);
      setSelectedMail(await r.json());
    } catch {
      setSelectedMail(null);
    }
    setLoadingDetail(false);
  };

  const card = { background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' };

  if (loadingDetail) return (
    <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
      Mail wird geladen...
    </div>
  );

  if (selectedMail) return <MailDetail mail={selectedMail} onBack={() => setSelectedMail(null)} isSent={isSent} />;

  if (loading) return (
    <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
      {isSent ? 'Gesendete Mails' : 'Posteingang'} wird geladen...
    </div>
  );

  if (!emails || emails.length === 0) return (
    <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
      Keine E-Mails gefunden.
    </div>
  );

  return (
    <>
      {total > 0 && (
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 10 }}>
          {total} {isSent ? 'gesendete Mails' : 'Mails im Posteingang'}
        </div>
      )}
      <div style={card}>
        {emails.map((mail, i) => (
          <div key={mail.id} onClick={() => openMail(mail.id)} style={{
            display: 'flex', gap: 12, padding: '10px 4px', cursor: 'pointer',
            borderBottom: i < emails.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none',
            background: isSent ? 'transparent' : (mail.is_read ? 'transparent' : 'rgba(193,167,111,0.04)'),
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-cream)'}
          onMouseLeave={e => e.currentTarget.style.background = isSent ? 'transparent' : (mail.is_read ? 'transparent' : 'rgba(193,167,111,0.04)')}
          >
            {!isSent && (
              <div style={{ width: 6, flexShrink: 0, paddingTop: 6 }}>
                {!mail.is_read && (
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand-gold)' }} />
                )}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{
                  fontSize: 13, fontWeight: isSent ? 400 : (mail.is_read ? 400 : 600),
                  color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {isSent ? `An: ${mail.to_display}` : (mail.from_name || mail.from_email)}
                </span>
                {!isSent && mail.is_external && (
                  <span style={{
                    fontSize: 9, padding: '1px 5px', borderRadius: 3,
                    background: 'var(--brand-gold-10, #f5f0e0)', color: 'var(--brand-gold)',
                    fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0,
                  }}>extern</span>
                )}
                {mail.importance === 'high' && (
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#fde8e8', color: 'var(--brand-danger)', fontWeight: 600, flexShrink: 0 }}>wichtig</span>
                )}
                {!isSent && mail.flagged && <span style={{ fontSize: 12, flexShrink: 0 }}>&#9873;</span>}
                {mail.has_attachments && <span style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0 }}>&#128206;</span>}
              </div>
              <div style={{
                fontSize: 13, fontWeight: isSent ? 400 : (mail.is_read ? 400 : 500),
                color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2,
              }}>{mail.subject}</div>
              <div style={{
                fontSize: 12, color: 'var(--brand-text-muted)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{mail.preview}</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0, paddingTop: 2, whiteSpace: 'nowrap' }}>
              {timeAgo(isSent ? mail.sent : mail.received)}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}
          >Zurück</button>
          <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>Seite {page} von {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}
          >Weiter</button>
        </div>
      )}
    </>
  );
}


// ── Shared Mailbox: Suche ──
function SharedSearchTab({ token }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMail, setSelectedMail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const doSearch = async () => {
    if (!query.trim() || query.trim().length < 2) return;
    setLoading(true);
    setResults(null);
    setSelectedMail(null);
    try {
      const r = await fetch(`${API_URL}/mail/shared/search`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query.trim(), count: 30 }),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setResults(data.messages || []);
    } catch {
      setResults([]);
    }
    setLoading(false);
  };

  const openMail = async (messageId) => {
    setLoadingDetail(true);
    try {
      const r = await fetch(`${API_URL}/mail/shared/message/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`${r.status}`);
      setSelectedMail(await r.json());
    } catch {
      setSelectedMail(null);
    }
    setLoadingDetail(false);
  };

  const card = { background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' };

  if (loadingDetail) return (
    <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
      Mail wird geladen...
    </div>
  );

  if (selectedMail) return <MailDetail mail={selectedMail} onBack={() => setSelectedMail(null)} isSent={false} />;

  return (
    <div>
      {/* Search Input */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch()}
          placeholder="Betreff, Absender, Inhalt durchsuchen..."
          style={{
            flex: 1, padding: '10px 14px', fontSize: 13,
            border: '1px solid var(--brand-warm-gray)', borderRadius: 6,
            fontFamily: 'var(--font-body)', color: 'var(--brand-navy)',
            outline: 'none',
          }}
        />
        <button
          onClick={doSearch}
          disabled={loading || !query.trim() || query.trim().length < 2}
          style={{
            padding: '10px 20px', fontSize: 12, fontWeight: 600,
            background: loading ? 'var(--brand-warm-gray)' : 'var(--brand-navy)',
            color: '#fff', border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer',
            fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.5px',
          }}
        >
          {loading ? 'Suche...' : 'Suchen'}
        </button>
      </div>

      {/* Results */}
      {loading && (
        <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
          Suche läuft...
        </div>
      )}

      {!loading && results !== null && results.length === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
          Keine Ergebnisse für "{query}".
        </div>
      )}

      {!loading && results && results.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 10 }}>
            {results.length} Ergebnis{results.length !== 1 ? 'se' : ''}
          </div>
          <div style={card}>
            {results.map((msg, i) => {
              const sender = msg.from?.emailAddress || {};
              return (
                <div key={msg.id} onClick={() => openMail(msg.id)} style={{
                  display: 'flex', gap: 12, padding: '10px 4px', cursor: 'pointer',
                  borderBottom: i < results.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-cream)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: msg.isRead === false ? 600 : 400, color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sender.name || sender.address || '-'}
                      </span>
                      {msg.hasAttachments && <span style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0 }}>&#128206;</span>}
                      {msg.importance === 'high' && (
                        <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#fde8e8', color: 'var(--brand-danger)', fontWeight: 600, flexShrink: 0 }}>wichtig</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: msg.isRead === false ? 500 : 400, color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                      {msg.subject || '(Kein Betreff)'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {msg.bodyPreview || ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0, paddingTop: 2, whiteSpace: 'nowrap' }}>
                    {timeAgo(msg.receivedDateTime)}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!loading && results === null && (
        <div style={{ ...card, textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>&#128269;</div>
          <div style={{ fontSize: 13, color: 'var(--brand-text-muted)' }}>
            Durchsuche alle Mails von JS@fleet-immobilien.de nach Betreff, Absender oder Inhalt.
          </div>
        </div>
      )}
    </div>
  );
}


// ── Hauptkomponente ──
// ═══════════════════════════════════════════════════════════════
// POSTFACH LIGHT
// ═══════════════════════════════════════════════════════════════

function PersonalMailList({ token, folder, onSelectMail }) {
  const [emails, setEmails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => { setPage(1); }, [folder]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    const url = folder === 'inbox'
      ? `${API_URL}/mail/inbox?page=${page}&page_size=30`
      : `${API_URL}/mail/folder/${folder}/messages?page=${page}&page_size=30`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.status === 401) { setError('mail_not_configured'); setLoading(false); return null; }
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then(data => {
        if (!data) return;
        setEmails(data.messages || []);
        setTotalPages(data.total_pages || 1);
        setTotal(data.total || 0);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token, folder, page]);

  const card = { background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' };

  if (error === 'mail_not_configured') return (
    <div style={{ ...card, textAlign: 'center', padding: '3rem' }}>
      <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }}>&#9993;</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8 }}>Mail-Zugriff nicht eingerichtet</div>
      <div style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
        Bitte melde dich einmal ab und wieder an, um den Mail-Zugriff zu aktivieren.
      </div>
      <button onClick={() => { localStorage.removeItem('fleet_token'); window.location.href = '/login'; }}
        style={{ padding: '8px 20px', fontSize: 13, fontWeight: 500, background: 'var(--brand-navy)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
        Neu anmelden
      </button>
    </div>
  );

  if (loading) return (
    <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
      Wird geladen...
    </div>
  );

  if (!emails || emails.length === 0) return (
    <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
      Keine E-Mails in diesem Ordner.
    </div>
  );

  return (
    <>
      {total > 0 && <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 10 }}>{total} Mails</div>}
      <div style={card}>
        {emails.map((mail, i) => (
          <div key={mail.id} onClick={() => onSelectMail(mail)} style={{
            display: 'flex', gap: 12, padding: '10px 4px', cursor: 'pointer',
            borderBottom: i < emails.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none',
            background: mail.is_read ? 'transparent' : 'rgba(193,167,111,0.04)',
            transition: 'background 0.1s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-cream, #f9f7f2)'}
          onMouseLeave={e => e.currentTarget.style.background = mail.is_read ? 'transparent' : 'rgba(193,167,111,0.04)'}
          >
            <div style={{ width: 6, flexShrink: 0, paddingTop: 6 }}>
              {!mail.is_read && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand-gold)' }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: mail.is_read ? 400 : 600, color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {mail.from_name || mail.from_email}
                </span>
                {mail.is_external && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'var(--brand-gold-10, #f5f0e0)', color: 'var(--brand-gold)', fontWeight: 600, textTransform: 'uppercase', flexShrink: 0 }}>extern</span>}
                {mail.importance === 'high' && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#fde8e8', color: 'var(--brand-danger)', fontWeight: 600, flexShrink: 0 }}>wichtig</span>}
                {mail.flagged && <span style={{ fontSize: 12, flexShrink: 0 }}>&#9873;</span>}
                {mail.has_attachments && <span style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0 }}>&#128206;</span>}
              </div>
              <div style={{ fontSize: 13, fontWeight: mail.is_read ? 400 : 500, color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>{mail.subject}</div>
              <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mail.preview}</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0, paddingTop: 2, whiteSpace: 'nowrap' }}>{timeAgo(mail.received || mail.sent)}</div>
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>Zurück</button>
          <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>Seite {page} von {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}>Weiter</button>
        </div>
      )}
    </>
  );
}


function MailDetailEnhanced({ mailSummary, token, onBack, onCompose }) {
  const [mail, setMail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (!token || !mailSummary?.id) return;
    setLoading(true);
    fetch(`${API_URL}/mail/message/${mailSummary.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setMail(data); setLoading(false); })
      .catch(() => setLoading(false));
    // Load attachments
    fetch(`${API_URL}/mail/message/${mailSummary.id}/attachments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { attachments: [] })
      .then(data => setAttachments(data.attachments || []))
      .catch(() => {});
    // Mark as read
    fetch(`${API_URL}/mail/message/${mailSummary.id}/read?is_read=true`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }, [token, mailSummary]);

  const handleDelete = async () => {
    if (!window.confirm('E-Mail in den Papierkorb verschieben?')) return;
    setActionLoading('delete');
    await fetch(`${API_URL}/mail/message/${mailSummary.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setActionLoading(null);
    onBack();
  };

  const handleArchive = async () => {
    setActionLoading('archive');
    await fetch(`${API_URL}/mail/move/${mailSummary.id}`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ folder: 'archive' }),
    });
    setActionLoading(null);
    onBack();
  };

  const handleToggleRead = async () => {
    const newRead = mail ? !mail.isRead : true;
    await fetch(`${API_URL}/mail/message/${mailSummary.id}/read?is_read=${newRead}`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    if (mail) setMail({ ...mail, isRead: newRead });
  };

  const card = { background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' };
  const actionBtn = (label, onClick, loading, color) => (
    <button onClick={onClick} disabled={!!actionLoading} style={{
      padding: '6px 14px', fontSize: 11, fontWeight: 500, border: '1px solid var(--brand-warm-gray)',
      borderRadius: 4, background: color || '#fff', color: color ? '#fff' : 'var(--brand-navy)',
      cursor: actionLoading ? 'default' : 'pointer', opacity: actionLoading === loading ? 0.6 : 1,
      fontFamily: 'var(--font-body)',
    }}>{actionLoading === loading ? '...' : label}</button>
  );

  if (loading) return <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>Mail wird geladen...</div>;
  if (!mail) return <div style={{ ...card, textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>Mail nicht gefunden.</div>;

  const sender = mail.from?.emailAddress || {};
  const toList = (mail.toRecipients || []).map(r => r.emailAddress?.address).filter(Boolean);
  const ccList = (mail.ccRecipients || []).map(r => r.emailAddress?.address).filter(Boolean);
  const body = mail.body || {};
  const htmlContent = body.contentType === 'html' ? body.content : null;
  const textContent = body.contentType === 'text' ? body.content : (mail.bodyPreview || '');

  return (
    <div>
      {/* Navigation + Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <button onClick={onBack} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 12, fontWeight: 500,
          border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: 'pointer',
        }}>&#8592; Zurück</button>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {actionBtn('Antworten', () => onCompose({ mode: 'reply', originalMail: mail }), 'reply')}
          {actionBtn('Allen antworten', () => onCompose({ mode: 'replyAll', originalMail: mail }), 'replyAll')}
          {actionBtn('Weiterleiten', () => onCompose({ mode: 'forward', originalMail: mail }), 'forward')}
          {actionBtn('Archivieren', handleArchive, 'archive')}
          {actionBtn(mail?.isRead ? 'Ungelesen' : 'Gelesen', handleToggleRead, 'read')}
          {actionBtn('Löschen', handleDelete, 'delete', 'var(--brand-danger, #dc2626)')}
        </div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 16, lineHeight: 1.4 }}>{mail.subject || '(Kein Betreff)'}</div>
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 6 }}>
          <strong style={{ color: 'var(--brand-navy)' }}>Von:</strong> {sender.name || sender.address || '-'} {sender.address ? `<${sender.address}>` : ''}
        </div>
        {toList.length > 0 && <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 6 }}><strong style={{ color: 'var(--brand-navy)' }}>An:</strong> {toList.join(', ')}</div>}
        {ccList.length > 0 && <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 6 }}><strong style={{ color: 'var(--brand-navy)' }}>CC:</strong> {ccList.join(', ')}</div>}
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 16 }}>
          <strong style={{ color: 'var(--brand-navy)' }}>Datum:</strong> {new Date(mail.receivedDateTime).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div style={{ marginBottom: 16, padding: 12, background: 'var(--brand-cream, #f9f7f2)', borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8 }}>Anhänge ({attachments.length})</div>
            {attachments.map(att => (
              <a key={att.id} href={`${API_URL}/mail/message/${mailSummary.id}/attachments/${att.id}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', marginRight: 8, marginBottom: 4, fontSize: 12, color: 'var(--brand-navy)', background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 4, textDecoration: 'none' }}
                target="_blank" rel="noopener noreferrer"
                onClick={e => {
                  e.preventDefault();
                  fetch(`${API_URL}/mail/message/${mailSummary.id}/attachments/${att.id}`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.blob())
                    .then(blob => { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = att.name; a.click(); URL.revokeObjectURL(url); });
                }}>
                &#128206; {att.name} <span style={{ color: 'var(--brand-text-muted)', fontSize: 10 }}>({Math.round((att.size || 0) / 1024)} KB)</span>
              </a>
            ))}
          </div>
        )}

        {/* Body */}
        <div style={{ borderTop: '1px solid var(--brand-warm-gray)', paddingTop: 16 }}>
          {htmlContent ? (
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--brand-navy)', overflowWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: htmlContent }} />
          ) : (
            <pre style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--brand-navy)', whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontFamily: 'var(--font-body)', margin: 0 }}>{textContent}</pre>
          )}
        </div>
      </div>
    </div>
  );
}


function ComposeEmail({ token, composeData, onClose, onSent }) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!composeData) return;
    const orig = composeData.originalMail;
    if (composeData.mode === 'reply' && orig) {
      setTo(orig.from?.emailAddress?.address || '');
      setSubject(orig.subject?.startsWith('AW:') ? orig.subject : `AW: ${orig.subject || ''}`);
    } else if (composeData.mode === 'replyAll' && orig) {
      const sender = orig.from?.emailAddress?.address || '';
      const others = (orig.toRecipients || []).map(r => r.emailAddress?.address).filter(Boolean);
      const ccAddrs = (orig.ccRecipients || []).map(r => r.emailAddress?.address).filter(Boolean);
      setTo([sender, ...others].join(', '));
      setCc(ccAddrs.join(', '));
      setSubject(orig.subject?.startsWith('AW:') ? orig.subject : `AW: ${orig.subject || ''}`);
    } else if (composeData.mode === 'forward' && orig) {
      setSubject(orig.subject?.startsWith('WG:') ? orig.subject : `WG: ${orig.subject || ''}`);
      const fwdBody = orig.body?.content || orig.bodyPreview || '';
      setBody(`\n\n--- Weitergeleitete Nachricht ---\nVon: ${orig.from?.emailAddress?.name || ''} <${orig.from?.emailAddress?.address || ''}>\nBetreff: ${orig.subject || ''}\n\n${orig.body?.contentType === 'text' ? fwdBody : '(HTML-Inhalt)'}`);
    }
  }, [composeData]);

  const handleSend = async () => {
    if (!to.trim()) { setError('Empfänger fehlt.'); return; }
    setSending(true);
    setError(null);
    const orig = composeData?.originalMail;
    let url, payload;

    if (composeData?.mode === 'reply' || composeData?.mode === 'replyAll') {
      url = `${API_URL}/mail/reply/${orig.id}`;
      payload = { body, reply_all: composeData.mode === 'replyAll', is_html: false };
    } else if (composeData?.mode === 'forward') {
      url = `${API_URL}/mail/forward/${orig.id}`;
      payload = { to: to.split(',').map(s => s.trim()).filter(Boolean), comment: body };
    } else {
      url = `${API_URL}/mail/send`;
      payload = {
        to: to.split(',').map(s => s.trim()).filter(Boolean),
        cc: cc ? cc.split(',').map(s => s.trim()).filter(Boolean) : [],
        subject, body, is_html: false,
      };
    }

    try {
      const res = await fetch(url, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Fehler ${res.status}`); }
      onSent();
    } catch (e) { setError(e.message); }
    setSending(false);
  };

  const inputStyle = { width: '100%', padding: '8px 12px', fontSize: 13, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontFamily: 'var(--font-body)', boxSizing: 'border-box' };
  const card = { background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-navy)' }}>
          {composeData?.mode === 'reply' ? 'Antworten' : composeData?.mode === 'replyAll' ? 'Allen antworten' : composeData?.mode === 'forward' ? 'Weiterleiten' : 'Neue E-Mail'}
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--brand-text-muted)' }}>&#10005;</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--brand-navy)', width: 40 }}>An:</label>
          <input value={to} onChange={e => setTo(e.target.value)} placeholder="kontakt@example.de" style={inputStyle} />
        </div>
        {(composeData?.mode !== 'reply' && composeData?.mode !== 'forward') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--brand-navy)', width: 40 }}>CC:</label>
            <input value={cc} onChange={e => setCc(e.target.value)} placeholder="optional" style={inputStyle} />
          </div>
        )}
        {composeData?.mode !== 'reply' && composeData?.mode !== 'replyAll' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--brand-navy)', width: 40 }}>Betreff:</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Betreff" style={inputStyle} />
          </div>
        )}
      </div>

      <textarea value={body} onChange={e => setBody(e.target.value)} rows={10} placeholder="Nachricht schreiben..."
        style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, minHeight: 120 }} />

      {error && <div style={{ fontSize: 12, color: 'var(--brand-danger, #dc2626)', marginTop: 8 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={handleSend} disabled={sending}
          style={{ padding: '8px 24px', fontSize: 13, fontWeight: 600, background: 'var(--brand-navy)', color: '#fff', border: 'none', borderRadius: 6, cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.7 : 1 }}>
          {sending ? 'Wird gesendet...' : 'Senden'}
        </button>
        <button onClick={onClose}
          style={{ padding: '8px 16px', fontSize: 13, border: '1px solid var(--brand-warm-gray)', borderRadius: 6, background: '#fff', color: 'var(--brand-navy)', cursor: 'pointer' }}>
          Verwerfen
        </button>
      </div>
    </div>
  );
}


function PersonalSearchTab({ token, onSelectMail }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const doSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/mail/search`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query.trim() }),
      });
      const data = await res.json();
      setResults(data.results || data.messages || []);
    } catch (e) { setResults([]); }
    setLoading(false);
  };

  const card = { background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()}
          placeholder="E-Mails durchsuchen..." style={{ flex: 1, padding: '8px 14px', fontSize: 13, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontFamily: 'var(--font-body)' }} />
        <button onClick={doSearch} disabled={loading}
          style={{ padding: '8px 20px', fontSize: 13, fontWeight: 500, background: 'var(--brand-navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          {loading ? '...' : 'Suchen'}
        </button>
      </div>
      {results !== null && (
        results.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>Keine Ergebnisse.</div>
        ) : (
          <div style={card}>
            {results.map((mail, i) => (
              <div key={mail.id || i} onClick={() => onSelectMail(mail)} style={{
                display: 'flex', gap: 12, padding: '10px 4px', cursor: 'pointer',
                borderBottom: i < results.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-cream, #f9f7f2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mail.from_name || mail.from_email || '-'}</div>
                  <div style={{ fontSize: 13, color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>{mail.subject}</div>
                  <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mail.preview || mail.bodyPreview || ''}</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0 }}>{timeAgo(mail.received || mail.receivedDateTime)}</div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}


export default function Emails() {
  const { user, token } = useAuth();
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [activeTab, setActiveTab] = useState('mail');
  const [selectedMail, setSelectedMail] = useState(null);
  const [composeData, setComposeData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canTriage = isAdmin || isManager;

  const folders = [
    { id: 'inbox', label: 'Posteingang', icon: '&#9993;' },
    { id: 'sentitems', label: 'Gesendet', icon: '&#10148;' },
    { id: 'drafts', label: 'Entwürfe', icon: '&#9998;' },
    { id: 'deleteditems', label: 'Papierkorb', icon: '&#128465;' },
  ];

  const tabs = [
    { id: 'mail', label: 'Postfach' },
    { id: 'search', label: 'Suche' },
    ...(canTriage ? [{ id: 'triage', label: 'Triage' }] : []),
    ...(canTriage ? [{ id: 'briefing', label: 'Briefing' }] : []),
  ];

  const handleSelectMail = (mail) => { setSelectedMail(mail); };
  const handleBack = () => { setSelectedMail(null); setRefreshKey(k => k + 1); };
  const handleCompose = (data) => { setComposeData(data || {}); };
  const handleComposeClose = () => { setComposeData(null); };
  const handleSent = () => { setComposeData(null); setSelectedMail(null); setRefreshKey(k => k + 1); };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-headline)', fontSize: 22, color: 'var(--brand-navy)', marginBottom: 4 }}>Postfach</div>
          <div style={{ fontSize: 13, color: 'var(--brand-text-muted)' }}>{user?.email || ''}</div>
        </div>
        <button onClick={() => handleCompose(null)} style={{
          padding: '8px 20px', fontSize: 13, fontWeight: 600, background: 'var(--brand-navy)', color: '#fff',
          border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'var(--font-body)',
        }}>+ Neue E-Mail</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid var(--brand-warm-gray)' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSelectedMail(null); setComposeData(null); }}
            style={{
              padding: '10px 20px', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? 'var(--brand-navy)' : 'var(--brand-text-muted)',
              background: 'none', border: 'none', borderBottom: activeTab === tab.id ? '2px solid var(--brand-gold)' : '2px solid transparent',
              cursor: 'pointer', fontFamily: 'var(--font-body)', marginBottom: -2,
            }}>{tab.label}</button>
        ))}
      </div>

      {/* Compose Overlay */}
      {composeData !== null && (
        <div style={{ marginBottom: 20 }}>
          <ComposeEmail token={token} composeData={composeData} onClose={handleComposeClose} onSent={handleSent} />
        </div>
      )}

      {/* Mail Tab */}
      {activeTab === 'mail' && !composeData && (
        selectedMail ? (
          <MailDetailEnhanced mailSummary={selectedMail} token={token} onBack={handleBack} onCompose={handleCompose} />
        ) : (
          <div style={{ display: 'flex', gap: 20 }}>
            {/* Folder Sidebar */}
            <div style={{ width: 160, flexShrink: 0 }}>
              {folders.map(f => (
                <button key={f.id} onClick={() => setActiveFolder(f.id)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 13,
                    fontWeight: activeFolder === f.id ? 600 : 400,
                    color: activeFolder === f.id ? 'var(--brand-navy)' : 'var(--brand-text-muted)',
                    background: activeFolder === f.id ? 'var(--brand-cream, #f9f7f2)' : 'transparent',
                    border: 'none', borderRadius: 4, cursor: 'pointer', marginBottom: 2,
                    fontFamily: 'var(--font-body)',
                  }} dangerouslySetInnerHTML={{ __html: `${f.icon}&nbsp;&nbsp;${f.label}` }} />
              ))}
            </div>
            {/* Mail List */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <PersonalMailList key={`${activeFolder}-${refreshKey}`} token={token} folder={activeFolder} onSelectMail={handleSelectMail} />
            </div>
          </div>
        )
      )}

      {/* Search Tab */}
      {activeTab === 'search' && !composeData && (
        selectedMail ? (
          <MailDetailEnhanced mailSummary={selectedMail} token={token} onBack={handleBack} onCompose={handleCompose} />
        ) : (
          <PersonalSearchTab token={token} onSelectMail={handleSelectMail} />
        )
      )}

      {/* Triage Tab (Admin/Manager) */}
      {activeTab === 'triage' && canTriage && <TriageTab token={token} />}

      {/* Briefing Tab (Admin/Manager) */}
      {activeTab === 'briefing' && canTriage && <BriefingTab token={token} />}
    </div>
  );
}
